import React, {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
} from 'react';

import { fg } from '@atlaskit/platform-feature-flags/fg';

import { useQuickFindOpen } from '../store/quick-find';

export type PerceivedLatencyAttributes = {
	// Ordinary-effect observations of eligible results, not browser paint timings or standard INP.
	inputToResultsObservedDurationsMs?: number[];
	maxInputToResultsObservedMs?: number;
	meanInputToResultsObservedMs?: number;
	p75InputToResultsObservedMs?: number;
	p90InputToResultsObservedMs?: number;
	timeToFirstClickMs?: number;
	// First input to the first observed search/empty/error results, emitted once until clear.
	timeToFirstResultMs?: number;
};

export type ResultsKind = 'recent' | 'search' | 'empty' | 'error';

type QueryMeasurement = {
	inputTimestamps: number[];
	results: Array<{ kind: ResultsKind; observedAt: number }>;
};

type QuickFindPerceivedLatency = {
	enablePerceivedLatencyTracking: boolean;
	recordQueryChange: (eventTimestamp: number, isClear: boolean) => void;
	recordResultSelected: (eventTimestamp: number) => PerceivedLatencyAttributes;
	observeResults: (kind: ResultsKind) => void;
	takeResultsAttributes: () => PerceivedLatencyAttributes;
	resetResultsMeasurement: () => void;
	resetOnClear: () => void;
};

const NOOP_CONTEXT: QuickFindPerceivedLatency = {
	enablePerceivedLatencyTracking: false,
	recordQueryChange: () => {},
	recordResultSelected: () => ({}),
	observeResults: () => {},
	takeResultsAttributes: () => ({}),
	resetResultsMeasurement: () => {},
	resetOnClear: () => {},
};

const QuickFindPerceivedLatencyContext = createContext<QuickFindPerceivedLatency>(NOOP_CONTEXT);

const durationBetween = (startedAt: number, endedAt: number): number =>
	Math.max(0, Math.round(endedAt - startedAt));

type FirstResultState =
	| { status: 'idle' }
	| { status: 'pending'; startedAt: number }
	| { status: 'ready'; durationMs: number }
	| { status: 'reported' };

type FirstClickState =
	| { status: 'idle' }
	| { status: 'pending'; startedAt: number }
	| { status: 'handled' };

const getPercentile = (sortedValues: number[], percentile: number): number => {
	const index = Math.ceil((percentile / 100) * sortedValues.length) - 1;
	return sortedValues[Math.max(0, index)];
};

const getInputResponsivenessAttributes = ({
	inputTimestamps,
	results,
}: QueryMeasurement): PerceivedLatencyAttributes => {
	const durations = inputTimestamps.flatMap((inputTimestamp) => {
		const nextResult = results.find(({ observedAt }) => observedAt >= inputTimestamp);
		return nextResult ? [durationBetween(inputTimestamp, nextResult.observedAt)] : [];
	});
	if (durations.length === 0) {
		return {};
	}
	const sortedDurations = [...durations].sort((a, b) => a - b);
	return {
		inputToResultsObservedDurationsMs: durations,
		maxInputToResultsObservedMs: sortedDurations[sortedDurations.length - 1],
		meanInputToResultsObservedMs: Math.round(
			durations.reduce((total, duration) => total + duration, 0) / durations.length,
		),
		p75InputToResultsObservedMs: getPercentile(sortedDurations, 75),
		p90InputToResultsObservedMs: getPercentile(sortedDurations, 90),
	};
};

const EnabledQuickFindPerceivedLatencyProvider = ({
	children,
}: {
	children: ReactNode;
}): React.JSX.Element => {
	const quickFindOpen = useQuickFindOpen();
	const activeQueryRef = useRef<QueryMeasurement | undefined>(undefined);
	const firstResultStateRef = useRef<FirstResultState>({ status: 'idle' });
	const firstClickStateRef = useRef<FirstClickState>({ status: 'idle' });
	const previousQuickFindOpenRef = useRef(quickFindOpen);

	const resetResultsMeasurement = useCallback((): void => {
		activeQueryRef.current = undefined;
	}, []);

	const resetOnClear = useCallback((): void => {
		resetResultsMeasurement();
		firstResultStateRef.current = { status: 'idle' };
		firstClickStateRef.current = { status: 'idle' };
	}, [resetResultsMeasurement]);

	const recordQueryChange = useCallback(
		(eventTimestamp: number, isClear: boolean): void => {
			if (isClear) {
				resetOnClear();
				return;
			}

			if (firstResultStateRef.current.status === 'idle') {
				firstResultStateRef.current = { status: 'pending', startedAt: eventTimestamp };
			}
			if (firstClickStateRef.current.status === 'idle') {
				firstClickStateRef.current = { status: 'pending', startedAt: eventTimestamp };
			}
			if (!activeQueryRef.current) {
				activeQueryRef.current = {
					inputTimestamps: [],
					results: [],
				};
			}
			activeQueryRef.current.inputTimestamps.push(eventTimestamp);
		},
		[resetOnClear],
	);

	const observeResults = useCallback((kind: ResultsKind): void => {
		const observedAt = performance.now();
		const firstResult = firstResultStateRef.current;
		if (kind !== 'recent' && firstResult.status === 'pending') {
			firstResultStateRef.current = {
				status: 'ready',
				durationMs: durationBetween(firstResult.startedAt, observedAt),
			};
		}
		activeQueryRef.current?.results.push({ kind, observedAt });
	}, []);

	// Consume TTFR once until clear; only responsiveness resets with each shown event.
	const takeResultsAttributes = useCallback((): PerceivedLatencyAttributes => {
		const measurement = activeQueryRef.current;
		activeQueryRef.current = undefined;
		const firstResult = firstResultStateRef.current;
		const attributes: PerceivedLatencyAttributes = {};
		if (firstResult.status === 'ready') {
			attributes.timeToFirstResultMs = firstResult.durationMs;
			firstResultStateRef.current = { status: 'reported' };
		}
		return {
			...attributes,
			...(measurement ? getInputResponsivenessAttributes(measurement) : {}),
		};
	}, []);

	const recordResultSelected = useCallback(
		(eventTimestamp: number): PerceivedLatencyAttributes => {
			const firstClickState = firstClickStateRef.current;
			const shouldMeasure = firstClickState.status === 'pending';
			firstClickStateRef.current = { status: 'handled' };
			resetResultsMeasurement();

			return shouldMeasure
				? { timeToFirstClickMs: durationBetween(firstClickState.startedAt, eventTimestamp) }
				: {};
		},
		[resetResultsMeasurement],
	);

	useEffect(() => {
		if (previousQuickFindOpenRef.current && !quickFindOpen) {
			resetResultsMeasurement();
		}
		previousQuickFindOpenRef.current = quickFindOpen;
	}, [quickFindOpen, resetResultsMeasurement]);

	const value = useMemo(
		() => ({
			enablePerceivedLatencyTracking: true,
			recordQueryChange,
			recordResultSelected,
			observeResults,
			takeResultsAttributes,
			resetResultsMeasurement,
			resetOnClear,
		}),
		[
			recordQueryChange,
			recordResultSelected,
			observeResults,
			takeResultsAttributes,
			resetResultsMeasurement,
			resetOnClear,
		],
	);

	return (
		<QuickFindPerceivedLatencyContext.Provider value={value}>
			{children}
		</QuickFindPerceivedLatencyContext.Provider>
	);
};

export const QuickFindPerceivedLatencyProvider = ({
	children,
}: {
	children: ReactNode;
}): React.JSX.Element => {
	if (!fg('qf_perceived_latency_instrumentation')) {
		return <>{children}</>;
	}

	return (
		<EnabledQuickFindPerceivedLatencyProvider>{children}</EnabledQuickFindPerceivedLatencyProvider>
	);
};

export const useQuickFindPerceivedLatency = (): QuickFindPerceivedLatency =>
	useContext(QuickFindPerceivedLatencyContext);
